import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom/cjs/react-router-dom';
import {FILTER_SECTIONS} from '../../../shared/components/filters/dropdown_section';
import {getFiltersAlphabetically} from '../../../shared/components/filters/filter_util';
import {buildHeaderBar} from '../../../shared/components/headers/header-bar/header_bar';
import {TopHeaderBar} from '../../../shared/components/headers/top-header-bar/TopHeaderBar';
import {GROUP_TYPE, TaskTable} from '../../../shared/components/task-tables/task-table-v3/TaskTable';
import {CreateTaskSetDrawer} from './task-set/CreateTaskSetDrawer';
import {ManageTaskSetsDrawer} from './task-set/ManageTaskSetsDrawer';
import {TaskSetChip} from './task-set/TaskSetChip';
import {plannedInstanceCount} from './task-set/useTaskSetPreview';
import {TYPES} from '../../../shared/components/task-tables/task-table-v3/Theme/NodeContentRenderer';
import {
	BUDGET_TYPE,
	BUTTON_COLOR,
	BUTTON_STYLE,
	ELEMENT_TYPE,
	ESTIMATION_UNIT,
	FILTER_SECTION,
	FILTER_TYPE,
	MODULE_TYPES,
} from '../../../../constants';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {getDeliverableEstimateRefs} from '../../../../components/baseline/Util';
import {withSocketHandling} from '../../../../socket/withSocketHandling';
import * as tracking from '../../../../tracking';
import Util from '../../../shared/util/util';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {createToast} from '../../../shared/components/toasts/toast';
import GenericTaskContextMenu from '../../../shared/components/context-menus/generic_task_context_menu';
import {
	getExpandedPhasesMap,
	getExpandedTaskMap,
	getHiddenFinancialFields,
	getInitialGroupType,
	getInitialSelectedColumns,
	getInitialSelectedTab,
	getPhasesFilters,
	tabs,
	updateSelectTasks,
} from './ProjectScopingUtil';
import {canDrag, getCanDropFunc, onMoveNode} from '../../../shared/components/task-tables/task-table-v3/table-util/TableLogic';
import {
	generateTree,
	getProjectTotalData,
	getSharedOptions,
	phaseActiveFilter,
	searchFilter,
} from '../../../shared/components/task-tables/task-table-v3/table-util/TableUtil';
import {getSocketConfig} from './ProjectScopingSocket';
import {useDataExport} from '../../../shared/hooks/useDataExport';
import TaskFormatter from '../../../shared/util/export-formatters/TaskFormatter';
import {hasPermission, hasSomePermission} from '../../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';
import {ProjectScopingTotals} from './totals-header/ProjectScopingTotals';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import {scopingPredictionsQuery} from './ProjectScopingPredictionsQuery';
import {shouldShowTaskOverrun, trackPredictionsShown} from '../../../shared/util/PredictionUtil';
import {keyBy} from 'lodash';
import {ProjectScopingTotalsProvider} from './totals-header/ProjectScopingTotalsContext';
import {BulkSelectPopup} from '@/web-components';
import {getComplexBulkOptions} from '../../../shared/util/BulkUtil';
import {getAdditionalBulkUpdateAutomateOptions, getBulkUpdateOptions} from './ProjectScopingBulkLogic';
import {useRecentProjects} from '../hooks/useRecentProjects';
import useUpdateEffect from '../../../shared/hooks/useUpdateEffect';
import {useTrackPage} from '../../../../tracking/amplitude/hooks/useTrackPage';
import {trackCSVExport, trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {createAvailabilityMap, getHolidaysMap} from './ResourceUtil';
import ProjectHeader from '../shared/ProjectHeader';
import {FlexColumn, SubHeading} from '@forecast-it/design-system';
import {eyeOptionsToCols, groupEyeOptions} from './ProjectScopingEyeUtil';
import {hasModule} from '../../../shared/util/ModuleUtil';
import {projectUrl} from '../../../../directApi';
import CompanySetupUtil from '../../../shared/util/CompanySetupUtil';
import {getProjectIndicatorString} from '../../../shared/components/project-indicator/support/ProjectIndicatorLogic';
import {optionTraverser} from '../../../../the_eye_util';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import ProjectUtil from '@/forecast-app/shared/util/ProjectUtil';
import moment from 'moment';
import {RateCardUtil} from '../../../shared/util/RateCardUtil';

const shouldShowResolvedTasksOption = project => {
	return hasFeatureFlag('scoping_no_stale_tasks') && project.isNoStaleTasksAllowed;
};

const getOrderedPhases = project => {
	if (project && project.phases && project.phases.edges)
		return ProjectUtil.sortPhasesBySortOrder(project.phases.edges).map(edge => edge.node.id);

	return [];
};

/** Sticky header plus roughly four rows: enough to see the list and scroll it. */
const TASK_TABLE_MIN_HEIGHT = 240;

export const ProjectScopingPage = ({viewer, history, relay, refetch, setSocketConfig}) => {
	const intl = useIntl();

	// Set as recent project
	let recentProjects = useRecentProjects();
	useEffect(() => {
		recentProjects.projectVisited(viewer.project.id);
	}, [viewer.project.id]);

	const showComplexBulkOptions = !viewer.company.sageIntacctConnectionEnabled;

	const shouldShowOverrunPrediction = shouldShowTaskOverrun(
		viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS,
		PERMISSION_TYPE.PHASE_UPDATE,
		viewer.project?.demo
	);
	const preAppliedFilters =
		viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS && Util.getUrlQueryParameter('show-overrunning')
			? {
					task: {
						predictedOverrun: shouldShowOverrunPrediction
							? ['predicted-overrun', 'currently-overrun']
							: ['currently-overrun'],
					},
			  }
			: null;
	const baselineRoleFinancialNumbers = viewer.project.baselineFinancialNumbers.filter(
		financialNumbers => financialNumbers.phaseBaselineRoleId !== null
	);
	const totalFinancialNumbers = viewer.project.totalFinancialNumbers;

	const hiddenFinancialFields = getHiddenFinancialFields(viewer.project.financialSourceSettings, intl);
	const isBaselineProject = Util.isBaselineProject(viewer.company.modules, viewer.project);

	// eslint-disable-next-line
	const [lazyDataFetched, setLazyDataFetched] = useState(true);
	// undefined = no drawer open. null = open for the No Phase group, which is a
	// supported case rather than a missing value, so the two must stay distinct.
	const [taskSetDrawerPhaseId, setTaskSetDrawerPhaseId] = useState(undefined);
	// One piece of state for the whole task-set surface. The toolbar button opens
	// it on the list; the chip on an instance opens it on that set. The drawer owns
	// which pane is showing from there. `nonce` is what makes re-clicking the SAME
	// chip reopen it -- see ManageTaskSetsDrawer's openNonce.
	const [manageDrawer, setManageDrawer] = useState({open: false, setId: null, nonce: 0});
	const [orderedPhaseIds, setOrderedPhaseIds] = useState(getOrderedPhases(viewer.project));
	const getInitialPhaseExpansionState = () => {
		const localStorageItem = Util.localStorageGetItem(viewer.project.id + '-phaseMap');
		return localStorageItem && localStorageItem !== '{}'
			? new Map(JSON.parse(localStorageItem))
			: getExpandedPhasesMap(viewer.project.phases.edges.map(phase => phase.node));
	};

	const getInitialTaskExpansionState = () => {
		const localStorageItem = Util.localStorageGetItem(viewer.project.id + '-taskMap');
		return localStorageItem && localStorageItem !== '{}'
			? new Map(JSON.parse(localStorageItem))
			: getExpandedTaskMap(viewer.project.tasks.edges.map(task => task.node));
	};

	// Expansion maps
	const [phasesExpansionMap, setPhasesExpansionMap] = useState(getInitialPhaseExpansionState());
	const [tasksExpansionMap, setTasksExpansionMap] = useState(getInitialTaskExpansionState());

	const [personGroupExpansionMap, setPersonGroupExpansionMap] = useState(new Map());
	const [roleGroupExpansionMap, setRoleGroupExpansionMap] = useState(new Map());
	const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab());
	const [filterFunctions, setFilterFunctions] = useState(getPhasesFilters(viewer.project.id, preAppliedFilters));
	const [searchFilterValue, setSearchFilterValue] = useState('');

	// saving state in ref also
	const [selectedTasks, setSelectedTasks] = useState([]);

	// The eye
	const [showBaselineInfo, setShowBaselineInfo] = useState(
		isBaselineProject
			? JSON.parse(localStorage.getItem('project-phases-show-baseline-info' + viewer.project.companyProjectId)) ?? true
			: false
	);
	// Whether this person wants the Nova column on this surface. Its own key rather than a column in
	// `project-new-scoping-selected-cols`, because Nova is not one of availableColumns — it renders
	// in the row's left sticky cluster, so horizontal scroll can never hide it. Not scoped to the
	// project, unlike the baseline-info toggle beside it: the preference is about this person and
	// this surface, not about the project they happen to be looking at.
	const [showNovaColumn, setShowNovaColumn] = useState(
		JSON.parse(localStorage.getItem('project-scoping-show-nova-column')) ?? true
	);
	const [groupSetting, setGroupSetting] = useState(getInitialGroupType());

	const [displayScopingAvailability, setDisplayScopingAvailability] = useState(
		JSON.parse(localStorage.getItem('project-phases-display-scoping-availability' + viewer.project.companyProjectId)) ??
			true
	);

	const SHOW_PERIODS_SETTING = 'project-scoping-show-periods';
	const SHOW_EMPTY_PERIODS_SETTING = 'project-scoping-show-empty-periods';

	/**
	 * Is this a retainer project, and are you allowed to see it.
	 */
	const isRetainerProject =
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]) &&
		viewer.project.budgetType === BUDGET_TYPE.RETAINER;

	const [showPeriods, setShowPeriods] = useState(
		localStorage.getItem(SHOW_PERIODS_SETTING)
			? JSON.parse(localStorage.getItem(SHOW_PERIODS_SETTING)) && isRetainerProject
			: false
	);
	const [showEmptyPeriods, setShowEmptyPeriods] = useState(
		localStorage.getItem(SHOW_EMPTY_PERIODS_SETTING)
			? JSON.parse(localStorage.getItem(SHOW_EMPTY_PERIODS_SETTING)) && isRetainerProject
			: true
	);

	const [showContextMenu, setShowContextMenu] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState(null);
	const [contextMenuTask, setContextMenuTask] = useState(null);
	const [createSubtaskTask, setCreateSubtaskTask] = useState(null);

	const [availableColumns, setAvailableColumns] = useState(
		getInitialSelectedColumns(intl.formatMessage, viewer, showPeriods)
	); // Consider refactoring to new approach to the eye

	const showResolvedTasksOption = shouldShowResolvedTasksOption(viewer.project);
	const [showResolvedTasks, setShowResolvedTasks] = useState(false);

	// Refs

	const isMounted = useRef(false);

	const {fetch, data: predictionData} = useForecastFetchQuery(scopingPredictionsQuery);

	const exportData = useDataExport(
		new TaskFormatter(
			intl,
			['phase_name'],
			[
				'chip_right',
				'expand',
				'phase',
				'selector',
				'starred',
				'context_menu',
				'show_scoping_project_total',
				'show_overrun_prediction',
			],
			true
		),
		null,
		data => data,
		_ => `${viewer.project.name}_scoping_data`
	);

	const currencySymbol = Util.GetCurrencySymbol(
		viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency
	);

	// A rate card is versioned: every rate row carries a startDate and a role may
	// have several, so "the rate for this role" is only answerable against a date.
	// RateCardUtil already indexes the rows that way and resolves the card's
	// default for a role the card does not price, so the task set drawer is handed
	// the lookup rather than a flattened table it would have to re-derive.
	//
	// Zero is reported as null: it is what the helper returns both for a genuinely
	// free rate and for a card that prices nothing on that date, and a revenue
	// figure of 0 reads as "free" either way. Better to state nothing.
	const rateCardUtil = useMemo(() => new RateCardUtil(viewer.project.rateCard), [viewer.project.rateCard]);
	const rateForRoleOnDate = useCallback(
		(roleId, date) => {
			const rate = rateCardUtil.getRoleRateOrDefaultOnDate(roleId, moment(date));
			return rate ? rate : null;
		},
		[rateCardUtil]
	);

	const predictionDataMap = useMemo(() => {
		if (predictionData) {
			const {viewer} = predictionData;
			const {predictionProject} = viewer;
			if (predictionProject && predictionProject.predictedOverrunningTasks) {
				const formattedOverrunningTasks = predictionProject.predictedOverrunningTasks.map(oTask => ({
					taskId: oTask.globalTaskId,
					predictedEstimate: {estimate: oTask.estimate, overrunProbability: oTask.overrunProbability},
				}));
				return keyBy(formattedOverrunningTasks, o => o.taskId);
			}
		}
	}, [predictionData]);

	const toggleCollapseAll = shouldCollapse => {
		const map = new Map();
		phasesExpansionMap.forEach((v, k) => map.set(k, !shouldCollapse));
		setPhasesExpansionMap(map);
		trackEvent('Scoping Phases', shouldCollapse ? 'Collapsed' : 'Expanded');
	};

	const togglePhaseExpanded = phaseId => {
		const map = new Map(phasesExpansionMap);
		const curVal = map.get(phaseId);
		map.set(phaseId, !curVal);
		setPhasesExpansionMap(map);
		trackEvent('Scoping Phase', curVal ? 'Collapsed' : 'Expanded');
		// Update expansion in localStorage
		Util.localStorageSetItem(viewer.project.id + '-phaseMap', JSON.stringify(Array.from(map)));
	};

	const toggleTaskExpanded = taskId => {
		const map = new Map(tasksExpansionMap);
		const curVal = map.get(taskId);
		map.set(taskId, !curVal);

		setTasksExpansionMap(map);
		// Update expansion in localStorage
		Util.localStorageSetItem(viewer.project.id + '-taskMap', JSON.stringify(Array.from(map)));
		if (taskId === createSubtaskTask) {
			setCreateSubtaskTask(null);
		}
	};

	/**
	 * Toggle expanded on person group
	 * @param  personGroupId
	 */
	const togglePersonGroupExpanded = personGroupId => {
		const map = new Map(personGroupExpansionMap);
		let curVal = map.get(personGroupId);
		//curVal will be undefined the first time the group is toggled
		//and by default groups are expanded
		if (curVal === undefined) {
			map.set(personGroupId, false);
		} else {
			map.set(personGroupId, !curVal);
		}
		setPersonGroupExpansionMap(map);
	};

	/**
	 * Toggle expanded on role group
	 * @param  roleGroupId
	 */
	const toggleRoleGroupExpanded = roleGroupId => {
		const map = new Map(roleGroupExpansionMap);
		let curVal = map.get(roleGroupId);
		//curVal will be undefined the first time the group is toggled
		//and by default groups are expanded
		if (curVal === undefined) {
			map.set(roleGroupId, false);
		} else {
			map.set(roleGroupId, !curVal);
		}
		setRoleGroupExpansionMap(map);
	};

	const toggleExpansion = useCallback(
		({node}) => {
			if (node.type === TYPES.HEADER) {
				togglePhaseExpanded(node.id);
			} else if (node.type === TYPES.TASK) {
				toggleTaskExpanded(node.id);
			} else if (node.type === TYPES.PERSON_GROUPING) {
				togglePersonGroupExpanded(node.id);
			} else if (node.type === TYPES.ROLE_GROUPING) {
				if (showBaselineInfo && node.phaseId !== 'no-phase') {
					toggleRoleGroupExpanded(node.role.id + '#' + node.phaseId);
				} else {
					toggleRoleGroupExpanded(node.id);
				}
			}
		},
		[phasesExpansionMap, tasksExpansionMap, createSubtaskTask, personGroupExpansionMap, roleGroupExpansionMap]
	);

	const showTaskModal = useCallback(
		taskId => {
			Util.showTaskModal(taskId, history);
		},
		[history]
	);

	let treeData = null;

	const handleRowSelected = useCallback(
		(taskToSelect, shiftKey) => {
			updateSelectTasks(
				viewer,
				intl,
				selectedTasks,
				taskToSelect,
				shiftKey,
				createToast,
				setSelectedTasks,
				groupSetting,
				treeData
			);
		},
		[viewer, selectedTasks, groupSetting]
	);

	const closeContextMenu = () => {
		if (showContextMenu) {
			setShowContextMenu(false);
			setContextMenuPosition(null);
			setContextMenuTask(null);
		}
	};

	const onContextMenu = useCallback((task, taskProject, e) => {
		const el = e.target;
		if (task.readOnly?.isReadOnly) {
			return;
		}
		//clicked is when the context menu is open from the 3 dot-options on table-row
		if (
			el &&
			(el.tagName === 'INPUT' ||
				el.tagName === 'BUTTON' ||
				el.tagName === 'A' ||
				(el.className.toLowerCase && el.className.toLowerCase().includes('clicked')))
		) {
			closeContextMenu();
			return;
		}
		e.preventDefault();

		const contextMenuPosition = {};

		const btn = document.elementFromPoint(e.pageX, e.pageY);
		if (btn != null && e.type !== 'contextmenu') {
			const btnBounds = btn.getBoundingClientRect();
			contextMenuPosition.x = btnBounds.right - btn.clientWidth - 2;
			if (window.innerHeight - btnBounds.bottom < 250) {
				contextMenuPosition.y = btnBounds.bottom - 282;
			} else {
				contextMenuPosition.y = btnBounds.bottom + 2;
			}
		} else {
			contextMenuPosition.x = e.pageX;
			//check if there is place for menu underneath cursor
			if (window.innerHeight - e.pageY < 250) {
				contextMenuPosition.y = e.pageY - 250;
			} else {
				contextMenuPosition.y = e.pageY;
			}
		}
		// If the context menu is opened from somewhere other than the 3 dot-options, clear bulk select.
		if (el.className.toLowerCase && !el.className.toLowerCase().includes('dot-options')) {
			setSelectedTasks([]);
		}
		setShowContextMenu(true);
		setContextMenuPosition(contextMenuPosition);
		setContextMenuTask(task);
	}, []);

	const onCreateSubtask = useCallback(
		taskId => {
			if (!tasksExpansionMap.get(taskId)) {
				toggleTaskExpanded(taskId);
			}
			setCreateSubtaskTask(createSubtaskTask === taskId ? null : taskId);
		},
		[createSubtaskTask, tasksExpansionMap]
	);

	const newPhaseId = useRef(null);

	const phases = useMemo(() => {
		return viewer.project.phases.edges
			.map(phase => phase.node)
			.filter(phase => phaseActiveFilter(phase, selectedTab))
			.sort((a, b) => {
				return orderedPhaseIds.indexOf(a.id) - orderedPhaseIds.indexOf(b.id);
			});
	}, [viewer.project.phases.edges, selectedTab, orderedPhaseIds]);

	const sharedOptions = useMemo(() => {
		const visibility = {
			showBaselineInfo,
			showPeriods: showPeriods,
			showEmptyPeriods: showEmptyPeriods,
			displayScopingAvailability,
			groupSetting,
		};

		return {
			...getSharedOptions(
				viewer,
				viewer.projectGroup,
				availableColumns,
				visibility,
				phases,
				createSubtaskTask,
				showTaskModal,
				onContextMenu,
				relay.refetch,
				handleRowSelected,
				onCreateSubtask,
				predictionData,
				selectedTasks.length > 0,
				intl
			),
			// Spread on rather than passed through getSharedOptions, which already
			// takes fourteen positional arguments. The add-task node reads it and the
			// quick-add row shows its link only when it is present.
			onCreateTaskSet: phaseId => setTaskSetDrawerPhaseId(phaseId),
			// The table renders whatever this returns under the task name, and null
			// for an ordinary task. Supplied here rather than imported by the table
			// so the shared table stays unaware of task sets.
			renderTaskSetChip: task =>
				hasFeatureFlag('recurring_tasks_set') && task && task.taskSet ? (
					<TaskSetChip
						taskSetId={task.taskSet.id}
						sequence={task.taskSetSequence}
						instanceCount={task.taskSet.instanceCount}
						// Only a count bound states how many instances the rule produces.
						// A date-bounded set's count is whatever the create form happened
						// to hold, and reading it as the series length made every instance
						// past it lose its label -- "1 of 1" on the first task and a bare
						// icon on the rest. The live count is the answer for those. A
						// weekly count is of weeks, so the total is sized from the rule.
						plannedCount={plannedInstanceCount(task.taskSet)}
						onOpen={setId => setManageDrawer(state => ({open: true, setId, nonce: state.nonce + 1}))}
					/>
				) : null,
		};
	}, [
		viewer,
		selectedTasks,
		availableColumns,
		showBaselineInfo,
		phases,
		createSubtaskTask,
		tasksExpansionMap,
		predictionData,
		showPeriods,
		showEmptyPeriods,
		displayScopingAvailability,
	]);

	const holidaysMap = getHolidaysMap(viewer.company.holidayCalendars);
	const allocations = viewer.project.allocations.edges.map(edge => edge.node);
	const projectPersons = viewer.project.projectPersons.edges.map(edge => edge.node);
	const availabilityMap = createAvailabilityMap(phases, allocations, projectPersons, holidaysMap, sharedOptions);

	treeData = useMemo(() => {
		return generateTree(
			viewer,
			phases,
			phasesExpansionMap,
			tasksExpansionMap,
			selectedTasks,
			availableColumns,
			groupSetting,
			filterFunctions,
			searchFilterValue,
			predictionDataMap,
			sharedOptions,
			intl,
			personGroupExpansionMap,
			roleGroupExpansionMap,
			availabilityMap,
			baselineRoleFinancialNumbers
		);
	}, [
		viewer,
		phases,
		phasesExpansionMap,
		tasksExpansionMap,
		personGroupExpansionMap,
		roleGroupExpansionMap,
		availableColumns,
		groupSetting,
		filterFunctions,
		searchFilterValue,
		predictionDataMap,
		selectedTasks,
		sharedOptions,
		showPeriods,
	]);

	const projectTotalData = useMemo(() => {
		return getProjectTotalData(treeData, viewer.project, viewer.availableFeatureFlags);
	}, [treeData, viewer.project, viewer.availableFeatureFlags]);

	// Calculate progress percentage for display in header
	const progressPercentage = useMemo(() => {
		if (!projectTotalData) return 0;
		if (projectTotalData.manualProgressEnabled) {
			return Math.round(projectTotalData.progress || 0);
		}
		return Math.round(projectTotalData.rolledUpData?.projectProgress || 0);
	}, [projectTotalData]);

	const handleOnEscapeKeydown = () => {
		setSelectedTasks([]);
	};

	const handleKeyDownListener = useCallback(
		e => {
			const {key} = e;
			switch (key) {
				case 'Escape':
					handleOnEscapeKeydown();
					break;
			}
		},
		[selectedTasks]
	);

	const showAutomateModal = variant => {
		showModal({
			type: MODAL_TYPE.PROJECT_AUTOMATE,
			variant: variant,
			tasks: selectedTasks,
			intl: intl,
			labels: viewer.company.labels,
			roles: viewer.company.roles,
			persons: viewer.project.projectPersons.edges.map(edge => edge.node.person),
		});
	};

	const showManagePhasesModal = () => {
		showModal({
			type: MODAL_TYPE.PHASE_MANAGEMENT_MODAL,
			initialPhases: ProjectUtil.sortPhasesBySortOrder(viewer.project.phases.edges),
			project: viewer.project,
			baselineItems: [],
			onSave: result => {
				const sortedIds = ProjectUtil.sortPhasesBySortOrder(result.bulkUpdatePhases.project.phases.edges).map(
					edge => edge.node.id
				);
				newPhaseId.current = sortedIds[0] ?? null;
				setOrderedPhaseIds(sortedIds);
			},
		});
	};

	const showManageDeliverablesModal = () => {
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: viewer.project.deliverables?.edges,
			projectId: viewer.project.id,
			// The editor only offers the fixed-fee controls on a Time & Materials project, and it
			// reads that from this prop — omitting it hides them, since an unknown budget type is
			// treated as not-T&M rather than assumed to be one.
			budgetType: viewer.project.budgetType,
			currency: viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency,
			// The estimate rows are part of the page query, so this is always a list here; the helper's
			// undefined ("can't confirm") case only applies to openers that don't load them.
			baselineItems: getDeliverableEstimateRefs(viewer.project),
		});
	};

	const theEyeSelector = (selectedElem, _, __, changedOptions) => {
		const savedOptionValues = {};
		optionTraverser(changedOptions, (option, path) => {
			savedOptionValues[path] = option.checked;
		});

		// custom logic for showing baseline info.
		// this is project specific, and not a global setting
		// that is why we save it in its own localStorage
		let baselineInfoOption = showBaselineInfo;
		if (selectedElem.name === 'show-scoping-baseline-info') {
			baselineInfoOption = !showBaselineInfo;
		}
		setShowBaselineInfo(baselineInfoOption);

		let displayScopingAvailabilityOption = displayScopingAvailability;
		if (selectedElem.name === 'display-scoping-availability') {
			displayScopingAvailabilityOption = !displayScopingAvailability;
			setDisplayScopingAvailability(displayScopingAvailabilityOption);
		}

		let novaColumnOption = showNovaColumn;
		if (selectedElem.name === 'nova') {
			novaColumnOption = !showNovaColumn;
			setShowNovaColumn(novaColumnOption);
		}

		// To avoid messing too much with the state management of Scoping, we will continue to store the columns themselves instead of just the eyeOptions object.
		// Eventually, it would likely make sense to align the approach to eye options with the newer pages by using the useEyeOptions hook.
		const cols = eyeOptionsToCols(savedOptionValues, availableColumns);

		setAvailableColumns(cols);

		const theEye = cols.map(c => {
			return {
				id: c.name,
				checked: c.checked,
			};
		});

		localStorage.setItem(`project-phases-show-baseline-info${viewer.project.companyProjectId}`, baselineInfoOption);
		localStorage.setItem(
			`project-phases-display-scoping-availability${viewer.project.companyProjectId}`,
			displayScopingAvailabilityOption
		);
		Util.localStorageSetItem('project-scoping-show-nova-column', JSON.stringify(novaColumnOption));
		Util.localStorageSetItem('project-new-scoping-selected-cols', JSON.stringify(theEye));
	};

	const toggleGroupByPerson = () => {
		// Deselect tasks when changing grouping
		setSelectedTasks([]);
		const toggleOn = groupSetting !== GROUP_TYPE.PERSON;
		setGroupSetting(toggleOn ? GROUP_TYPE.PERSON : GROUP_TYPE.NO_GROUPING);

		Util.localStorageSetItem('project-scoping-group-by-person', toggleOn);
		Util.localStorageSetItem('project-scoping-group-by-role', false);

		tracking.trackElementClicked('Group by person', {enabled: toggleOn});
		trackEvent('Group By Person', toggleOn ? 'Enabled' : 'Disabled');
	};

	const toggleGroupByRole = () => {
		// Deselect tasks when changing grouping
		setSelectedTasks([]);
		const toggleOn = groupSetting !== GROUP_TYPE.ROLE;
		setGroupSetting(toggleOn ? GROUP_TYPE.ROLE : GROUP_TYPE.NO_GROUPING);

		Util.localStorageSetItem('project-scoping-group-by-role', toggleOn);
		Util.localStorageSetItem('project-scoping-group-by-person', false);

		tracking.trackElementClicked('Group by role', {enabled: toggleOn});
		trackEvent('Group By Role', toggleOn ? 'Enabled' : 'Disabled');
	};

	const toggleShowPeriods = () => {
		setShowPeriods(value => {
			const newValue = !value;
			Util.localStorageSetItem(SHOW_PERIODS_SETTING, newValue);
			tracking.trackElementClicked('Show Periods', {enabled: newValue});
			trackEvent('Show Periods', newValue ? 'Enabled' : 'Disabled');
			return newValue;
		});
	};

	const toggleShowEmptyPeriods = () => {
		setShowEmptyPeriods(value => {
			const newValue = !value;
			Util.localStorageSetItem(SHOW_EMPTY_PERIODS_SETTING, newValue);
			tracking.trackElementClicked('Show Empty Periods', {enabled: newValue});
			trackEvent('Show Empty Periods', newValue ? 'Enabled' : 'Disabled');
			return newValue;
		});
	};

	const toggleShowResolvedTasks = () => {
		const newValue = !showResolvedTasks;
		const willLoadResolvedTasks = newValue === true;

		const toggleResolvedTasks = () => {
			dispatch(EVENT_ID.SHOW_LOADER);

			trackEvent('Dormant Tasks', willLoadResolvedTasks ? 'Shown' : 'Hidden');

			// If dormant tasks are being loaded, stop socket until it is turned off again (socket events will likely be too large for the backend to handle)
			if (willLoadResolvedTasks) {
				setSocketConfig([]);
			} else {
				setSocketConfig(getSocketConfig(viewer.project.id, viewer.actualPersonId));
			}

			relay.refetch(
				{
					noStaleTasks: !newValue, // To avoid confusion - When showResolvedTasks is true, noStaleTasks should be false.
				},
				null,
				() => {
					dispatch(EVENT_ID.HIDE_LOADER);
					dispatch(EVENT_ID.CLOSE_MODAL);
					setShowResolvedTasks(() => {
						return newValue;
					});
				}
			);
		};

		if (willLoadResolvedTasks) {
			showModal({
				type: MODAL_TYPE.CONFIRMATION,
				title: 'Show Dormant Tasks',
				description: 'This is likely to be slow. Please be patient.',
				autoClosePositive: false,
				callbackPositive: () => toggleResolvedTasks(),
			});
		} else {
			toggleResolvedTasks();
		}
	};

	const handleTabChange = tab => {
		setSelectedTab(tab);
		trackEvent('Phases Dropdown', 'Changed', {value: tab.value});
		Util.localStorageSetItem('project-scoping-selected-tab', tab.value);
	};

	const onAutoScheduleButtonClicked = () => {
		tracking.trackElementClicked('Auto Schedule project from the Scoping page');
		trackEvent('Auto Schedule', 'Opened');
		//  open new tab on the project timeline and with the autoschedule modal
		window.open(`${projectUrl(viewer.project.companyProjectId)}/schedule#auto-schedule`);
	};

	const dataCSV = () => {
		const allTasks = viewer.project.tasks.edges;

		let csvTasks = allTasks.map(task => task.node);
		csvTasks = csvTasks.sort((a, b) => {
			const aPhase = a.phase ? a.phase : null;
			const bPhase = b.phase ? b.phase : null;
			const samePhase = aPhase && bPhase && aPhase.id === bPhase.id;

			// Both are in same phase, compare sort order
			if ((aPhase === null && bPhase === null) || samePhase) {
				return a.sortOrder - b.sortOrder;
			} else if (aPhase == null && bPhase) {
				return 1;
			} else if (aPhase && bPhase == null) {
				return -1;
			}
			//	Else sort by phase
			const aStartDate = aPhase.startYear
				? Util.CreateMomentDate(aPhase.startYear, aPhase.startMonth, aPhase.startDay)
				: null;
			const bStartDate = bPhase.startYear
				? Util.CreateMomentDate(bPhase.startYear, bPhase.startMonth, bPhase.startDay)
				: null;

			const sameDate = aStartDate && bStartDate && aStartDate.isSame(bStartDate);
			// Both phases have the same start date, sort by phase id
			if ((aStartDate == null && bStartDate == null) || sameDate) {
				return aPhase.id < bPhase.id ? 1 : -1;
			} else if (aStartDate == null && bStartDate) {
				return 1;
			} else if (aStartDate && bStartDate == null) {
				return -1;
			} else return aStartDate.isBefore(bStartDate) ? -1 : 1;
		});
		return csvTasks;
	};

	const exportCSV = () => {
		const hiddenColumnNames = ['total_availability', 'remaining_availability'];
		// Clone columns and replace - to _ in all columns to match CSV formatter.
		let columns = JSON.parse(JSON.stringify(availableColumns))?.filter(column => !hiddenColumnNames.includes(column.name));
		columns.forEach(column => (column.name = column.name.replaceAll('-', '_')));
		exportData(columns, dataCSV(), {projects: [viewer.project]}, null, 'CSV');
		trackCSVExport('Scoping');
	};

	const getHeaderTitleContent = () => {
		const content = [];
		const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

		const onboardingFlows = [
			{
				id: 'scoping-introduction',
				title: 'Introduction to the page',
				description: null,
				contentId: '1681821491mYer9167',
			},
			{
				id: 'scoping-first-phase',
				title: 'How to scope out your first phase',
				description: null,
				contentId: '1681888433yDqg606',
			},
		];

		const onboardingComponent = {
			id: 'onboarding-component',
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: intl.formatMessage({id: 'onboarding.scoping_onboarding_title'}),
			options: onboardingFlows,
			helpCenterLink: 'https://support.forecast.app/hc/en-us/sections/4419344459665-Projects',
			subLink: 'https://support.forecast.app/hc/en-us/articles/5302898580369-Defining-your-project-scope',
		};
		if (hiddenFinancialFields.length) {
			content.push({
				id: 'warning-component',
				type: TopHeaderBar.TYPE.WARNING,
				width: 'l',
				tooltip: (
					<FlexColumn gap={'xl'}>
						<SubHeading>{intl.formatMessage({id: 'financial.task_breakdown.finance_information_na'})}</SubHeading>
						<FormattedHTMLMessage
							id="financial.task_breakdown.finance_information_na_description"
							values={{
								link: `${location.pathname.split('/').slice(0, -1).join('/')}/settings#financials`,
							}}
						></FormattedHTMLMessage>
						<ul>
							{hiddenFinancialFields.map(field => (
								<li>{field}</li>
							))}
						</ul>
					</FlexColumn>
				),
			});
		}
		content.push(onboardingComponent);

		if (hasFinancialCategoriesUpdate) {
			content.push({
				id: 'progress-text',
				type: TopHeaderBar.TYPE.PROGRESS_TEXT,
				content: `${progressPercentage}% complete`,
			});
		}

		return content;
	};

	const constructHeaderBar = () => {
		const parentGroup = viewer.project && viewer.project.projectGroupId;

		const projectLocked = viewer.project.status === 'DONE' || viewer.project.status === 'HALTED';
		const createPhaseAccess = hasPermission(PERMISSION_TYPE.PHASE_CREATE);
		const {formatMessage} = intl;
		const leftContent = [];
		const rightContent = [];

		const autoSchedulingEnabled =
			hasFeatureFlag('auto_schedule_access') &&
			!viewer.project.useManualAllocations &&
			hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);
		tabs.forEach(tab => {
			tab.label = formatMessage({id: tab.translationId});
		});
		const pastOrActivePhase = {
			type: ELEMENT_TYPE.DROPDOWN_PHASES,
			dropdownOptions: tabs,
			value: selectedTab.value,
			buttonCy: 'new-ui-scoping-phase-selector-tab',
			listDataCy: 'new-ui-scoping-phase-selector-options',
			userpilot: 'milestone-dropdown',
			callback: tab => handleTabChange(tab),
		};
		rightContent.push(pastOrActivePhase);

		const customEyeOptions = [];

		if (viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS && isBaselineProject) {
			customEyeOptions.push({
				name: 'show-scoping-baseline-info',
				checked: showBaselineInfo,
				translationId: 'scoping.show_scoping_baseline_info',
			});
		}

		if (viewer.company.isUsingProjectAllocation || viewer.company.isUsingMixedAllocation) {
			customEyeOptions.push({
				name: 'display-scoping-availability',
				checked: displayScopingAvailability,
				translationId: 'scoping.display_scoping_availability',
			});
		}

		// Listed only where the feature is on. An account without Nova has no column for the option to
		// speak about, and an option that governs nothing is worse than no option at all.
		if (hasFeatureFlag('nova_agent_invocation')) {
			customEyeOptions.push({
				name: 'nova',
				checked: showNovaColumn,
				translationId: 'nova_invocation.column.header',
			});
		}

		const eyeAvailableColumns = groupEyeOptions(availableColumns.concat(customEyeOptions));

		const theEye = {
			type: ELEMENT_TYPE.THE_EYE,
			options: availableColumns.length > 0 ? eyeAvailableColumns : null,
			onSelect: theEyeSelector,
			openRight: true,
			ignoreScroll: true,
		};
		leftContent.push(theEye);

		if (!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) {
			const csv = {
				type: ELEMENT_TYPE.CSV,
				callback: () => exportCSV(),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.LIGHTGREY,
				text: formatMessage({id: 'common.export-csv'}),
				tooltipEnabled: true,
				tooltipProps: {
					autoPlace: true,
					grey: true,
					infoText: formatMessage({id: 'common.export-csv'}),
				},
				disabled: !lazyDataFetched,
			};
			leftContent.push(csv);
		}
		if (projectLocked) {
			const indicator = {
				type: ELEMENT_TYPE.INDICATOR,
				status: viewer.project.status,
			};
			leftContent.push(indicator);
		}

		const search = {
			type: ELEMENT_TYPE.SEARCH_LAZY,
			onChange: searchString => setSearchFilterValue(searchString),
			placeholder: intl.formatMessage({id: 'global_search.placeholder'}),
		};

		rightContent.push(search);

		const taskFilters = [
			FILTER_TYPE.PROJECT_PERSON,
			FILTER_TYPE.ROLE,
			FILTER_TYPE.PROJECT_STATUS_COLUMN,
			FILTER_TYPE.APPROUVED,
			FILTER_TYPE.DEADLINE,
			FILTER_TYPE.DEPENDENCIES,
			FILTER_TYPE.INDICATOR,
			FILTER_TYPE.LABEL,
			FILTER_TYPE.PROJECT_PHASE,
			FILTER_TYPE.RECENT_ACTIVITY,
		];

		if (viewer.company.teams.edges.length > 0) {
			taskFilters.push(FILTER_TYPE.TEAM);
		}

		if (viewer.project && viewer.project.sprintTimeBox) {
			taskFilters.push(FILTER_TYPE.PROJECT_SPRINT);
			taskFilters.push(FILTER_TYPE.SPRINT_CATEGORY);
		}

		if (viewer.project.useTaskOwner) {
			taskFilters.push(FILTER_TYPE.PROJECT_OWNER);
		}

		if (viewer.project.useTaskFollowers) {
			taskFilters.push(FILTER_TYPE.PROJECT_FOLLOWER);
		}

		if (viewer.project && viewer.project.taskLevels === 2) {
			taskFilters.push(FILTER_TYPE.SUB_TASKS);
		}

		if (viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS) {
			taskFilters.push(FILTER_TYPE.PREDICTED_OVERRUN);
		}

		const projectFilters = [];
		const peopleFilters = [];
		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: FILTER_SECTIONS.TASKS,
			projectFilters,
			peopleFilters,
			taskFilters: getFiltersAlphabetically(taskFilters, intl.formatMessage),
			primaryFilters: {
				[FILTER_SECTIONS.TASKS]: [
					FILTER_TYPE.PROJECT_STATUS_COLUMN,
					FILTER_TYPE.PROJECT_PERSON,
					FILTER_TYPE.PROJECT_PHASE,
					FILTER_TYPE.ROLE,
					FILTER_TYPE.DEADLINE,
					FILTER_TYPE.LABEL,
				],
			},
			viewer: viewer,
			preAppliedFilters: preAppliedFilters,
			appliedFiltersName: `project-scoping-filters-v4-${viewer.project.id}`,
			filterSection: FILTER_SECTION.SCOPING,
			onFiltersChange: (_, filterFunctions) => setFilterFunctions(filterFunctions),
			projectId: viewer.project.id,
			companyProjectId: viewer.project.companyProjectId,
		});

		if (createPhaseAccess) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'phase_management.manage_phases'}),
				callback: () => showManagePhasesModal(),
				disabled: projectLocked,
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				userpilot: 'manage-phases-button',
				dataCy: 'new-ui-scoping-manage-phases-button',
			});
		}

		if (hasFeatureFlag('recurring_tasks_set')) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'task_set.manage'}),
				callback: () => setManageDrawer(state => ({open: true, setId: null, nonce: state.nonce + 1})),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				dataCy: 'new-ui-scoping-manage-task-sets-button',
			});
		}

		if (ProjectUtil.deliverablesUiEnabled(viewer.project) && createPhaseAccess) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'deliverables.manage.modal.manage_deliverables'}),
				callback: () => showManageDeliverablesModal(),
				disabled: projectLocked,
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				userpilot: 'manage-deliverables-button',
				dataCy: 'new-ui-scoping-manage-deliverables-button',
			});
		}

		if (autoSchedulingEnabled && !parentGroup && !projectLocked) {
			const autoScheduleButton = {
				key: 'auto-schedule-button',
				type: ELEMENT_TYPE.BUTTON,
				customClassName: 'auto-scheduling-button',
				autoScheduleStyle: true,
				text: intl.formatMessage({id: 'auto_scheduling.auto_schedule'}),
				callback: () => onAutoScheduleButtonClicked(),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.AI_BLUE,
			};
			rightContent.push(autoScheduleButton);
		}

		const shouldCollapse = [...phasesExpansionMap.values()].some(val => val === true);
		const collapse = {
			type: ELEMENT_TYPE.COLLAPSE,
			collapsed: shouldCollapse,
			toggleCollapse: () => toggleCollapseAll(shouldCollapse),
		};

		rightContent.push(collapse);

		const actionsMenuOptions = [];

		actionsMenuOptions.push({
			key: 'GROUP_BY_PERSONS',
			text: formatMessage({id: 'common.group_by_person'}),
			hotkey: 'Shift + G',
			onClick: () => toggleGroupByPerson(),
			slider: true,
			checked: groupSetting === GROUP_TYPE.PERSON,
		});

		actionsMenuOptions.push({
			key: 'GROUP_BY_ROLE',
			text: formatMessage({id: 'common.group_by_role'}),
			onClick: () => toggleGroupByRole(),
			slider: true,
			checked: groupSetting === GROUP_TYPE.ROLE,
		});

		if (isRetainerProject) {
			actionsMenuOptions.push({
				key: 'SHOW_PERIODS',
				text: 'Show Periods',
				onClick: toggleShowPeriods,
				slider: true,
				checked: showPeriods,
			});
			actionsMenuOptions.push({
				key: 'SHOW_EMPTY_PERIODS',
				text: 'Show Empty Periods',
				onClick: toggleShowEmptyPeriods,
				slider: true,
				checked: showPeriods && showEmptyPeriods,
			});
		}

		if (showResolvedTasksOption) {
			actionsMenuOptions.push({
				key: 'SHOW_DORMANT_TASKS',
				text: 'Show Dormant Tasks',
				onClick: toggleShowResolvedTasks,
				slider: true,
				checked: showResolvedTasks,
			});
		}

		if (actionsMenuOptions.length > 0) {
			const actionsMenu = {
				type: ELEMENT_TYPE.ACTIONS_MENU,
				options: actionsMenuOptions,
				whiteInner: true,
				isWhite: true,
				blackIcon: true,
				userpilot: showResolvedTasksOption ? 'actions-menu-top-with-resolved-tasks' : 'actions-menu-top',
			};

			rightContent.push(actionsMenu);
		}

		return buildHeaderBar(leftContent, rightContent);
	};

	const selectedTaskCount = selectedTasks.length;

	const getContextMenu = () => {
		return (
			<GenericTaskContextMenu
				cy="scoping-task-context-menu"
				selectedTasks={selectedTasks}
				task={contextMenuTask}
				viewer={viewer}
				contextMenuPosition={contextMenuPosition}
				closeContextMenu={closeContextMenu}
				unselectTasks={() => setSelectedTasks([])}
			/>
		);
	};

	const canDrop = useMemo(() => {
		return getCanDropFunc(groupSetting, showBaselineInfo);
	}, [groupSetting, showBaselineInfo]);

	const hasFinancialAccess =
		CompanySetupUtil.hasFinance() &&
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]);

	const isUsingProjectAllocation = viewer.company.isUsingProjectAllocation;
	const isPointsProject = viewer.project.estimationUnit === ESTIMATION_UNIT.POINTS;

	// UseEffects

	useTrackPage('Project Scoping', {selectedTab: selectedTab.value});

	useEffect(() => {
		tracking.trackPage('project-scoping-v2');

		if (isMounted.current) setOrderedPhaseIds(getOrderedPhases(viewer.project));

		const name =
			viewer.project.name !== null && viewer.project.name !== ''
				? viewer.project.name
				: getProjectIndicatorString(viewer.project.companyProjectId, viewer.project.customProjectId);
		document.title = 'Scoping - ' + name + ' - Forecast';
		setSocketConfig(getSocketConfig(viewer.project.id, viewer.actualPersonId));
		if (shouldShowOverrunPrediction) {
			fetch({projectId: viewer.project.id});
		}

		if (refetch) {
			refetch(
				{
					fetchLazyData: true,
				},
				null,
				() => setLazyDataFetched(true)
			);
		}

		window.addEventListener('keydown', handleKeyDownListener);

		isMounted.current = true;

		return () => {
			window.removeEventListener('keydown', handleKeyDownListener);
		};
	}, [viewer.project.id]);

	useUpdateEffect(() => {
		const initialSelectedColumns = getInitialSelectedColumns(intl.formatMessage, viewer, showPeriods);
		setAvailableColumns(initialSelectedColumns);
	}, [showPeriods]);

	useEffect(() => {
		if (predictionData) {
			const tasks = viewer.project.tasks.edges;
			const predictionDataTasks = tasks.map(t => {
				return {
					...t.node,
					predictedEstimate: predictionDataMap[t.node.id]?.predictedEstimate,
				};
			});
			trackPredictionsShown(predictionDataTasks);
		}
	}, [predictionDataMap]);

	useEffect(() => {
		if (searchFilterValue && searchFilterValue !== '') {
			const taskCount = viewer.project.tasks.edges.filter(task => searchFilter(task, searchFilterValue)).length;
			trackEvent('Search', 'Results', {searchString: searchFilterValue, matching: taskCount});
		}
	}, [searchFilterValue]);

	return (
		<div
			className="section-content project-scoping no-margin"
			style={{flexGrow: 1, display: 'flex', flexDirection: 'column', overflowX: 'auto', overflowY: 'hidden'}} // Hiding overflowY to prevent outermost scrollbar from ever appearing. Outer scroll can cause a flickering loop because of the calculated height of the table combined with grid/flex elements outside the table.
			data-cy={'scoping-page'}
		>
			{selectedTaskCount > 0 && (
				<BulkSelectPopup
					itemCount={selectedTaskCount}
					counterText={intl.formatMessage({id: 'bulk_edit.tasks_selected'})}
					actionOptions={getBulkUpdateOptions(
						selectedTasks,
						setSelectedTasks,
						viewer.project,
						viewer.company,
						viewer.timerTask,
						viewer.timeStartDate,
						viewer.availableFeatureFlags,
						intl
					)}
					onClose={() => setSelectedTasks([])}
					complexOptions={
						showComplexBulkOptions
							? getComplexBulkOptions(
									variant => showAutomateModal(variant),
									getAdditionalBulkUpdateAutomateOptions(
										selectedTasks,
										setSelectedTasks,
										predictionDataMap,
										viewer.project,
										PERMISSION_TYPE.PHASE_UPDATE,
										viewer.availableFeatureFlags,
										intl
									),
									intl
							  )
							: null
					}
				/>
			)}
			<ProjectHeader
				title={intl.formatMessage({id: 'project_section.scope'})}
				titleContent={getHeaderTitleContent()}
				buttons={constructHeaderBar()}
				project={viewer.project}
				psProject={viewer.psProject}
				// Scoping is a single-project view, never a project-group one (unlike e.g. the
				// Workflow page, which doubles as both) — explicit null, not an omitted prop, is
				// what tells Relay's fragment resolver this is deliberate.
				projectGroup={null}
			/>
			<ProjectScopingTotalsProvider
				projectData={projectTotalData}
				settings={{
					currencySymbol,
					hasFinancialAccess,
					isUsingProjectAllocation,
					isPointsProject,
					isBaselineProject,
					financialSourceSettings: viewer.project.financialSourceSettings,
					useAllocationsForPlannedValues: viewer.project.financialSourceSettings.plannedHours === 'ALLOCATION',
					useAllocationsForActualValues: viewer.project.financialSourceSettings.actualHours === 'ALLOCATION',
				}}
				baselineFinancialNumbers={baselineRoleFinancialNumbers}
				totalFinancialNumbers={totalFinancialNumbers}
				phases={phases}
			>
				<ProjectScopingTotals />
			</ProjectScopingTotalsProvider>
			{showContextMenu ? getContextMenu() : null}
			{/* A floor, so the table is never squeezed to a sliver. It is a flex item in
			    a column, and its virtualised content reports a tiny minimum, so on a short
			    window the rows above it take everything and the list collapses to a few
			    pixels with no way to reach the tasks. The floor keeps the sticky header
			    plus a few rows visible and scrollable. minHeight rather than height so a
			    tall window still grows the list as before. */}
			<div style={{flexGrow: 1, display: 'flex', minHeight: TASK_TABLE_MIN_HEIGHT}}>
				<TaskTable
					newTopNode={newPhaseId.current}
					onToggleExpansion={toggleExpansion}
					groupType={groupSetting}
					showBaselineInfo={showBaselineInfo}
					useTaskHierarchy={sharedOptions.useTaskHierarchy}
					tableData={treeData}
					setSelectedTasks={setSelectedTasks}
					selectedTasks={selectedTasks}
					availableColumns={availableColumns}
					showNovaColumn={showNovaColumn}
					onMoveNode={onMoveNode}
					onVisibilityToggle={toggleExpansion}
					canDrag={canDrag}
					canDrop={canDrop}
					refetch={relay.refetch}
				/>
			</div>
			{hasFeatureFlag('recurring_tasks_set') ? (
				<ManageTaskSetsDrawer
					isOpen={manageDrawer.open}
					project={viewer.project}
					initialSetId={manageDrawer.setId}
					openNonce={manageDrawer.nonce}
					onClose={() => setManageDrawer(state => ({...state, open: false}))}
				/>
			) : null}
			{taskSetDrawerPhaseId !== undefined ? (
				<CreateTaskSetDrawer
					isOpen={true}
					projectId={viewer.project.id}
					phaseId={taskSetDrawerPhaseId}
					projectName={viewer.project.name}
					isPointsProject={isPointsProject}
					// All of these are already on this page's fragment for the table's own
					// dropdowns, so the drawer adds nothing to the query.
					roles={viewer.company.roles?.edges?.map(edge => edge.node) ?? []}
					assignablePersons={viewer.project.projectPersons.edges.map(edge => edge.node.person)}
					statusColumns={viewer.project.statusColumnsV2?.edges?.map(edge => edge.node) ?? []}
					labels={viewer.company.labels?.edges?.map(edge => edge.node) ?? []}
					projectBudgetType={viewer.project.budgetType}
					companyWorkingHours={viewer.company}
					rateForRoleOnDate={rateForRoleOnDate}
					// The RATE CARD's symbol, the same one every other money figure on
					// this page uses -- the totals are fetched with
					// convertToProjectCurrency: true and DatesAndRevenueTile renders
					// plannedRevenue with it. An earlier version of PIKA-672 converted the
					// drawer into base currency; that made it the only figure on the page
					// in a different currency from the tile it feeds.
					currencySymbol={currencySymbol}
					// TRUE for the budget types whose plannedRevenue accumulates from
					// estimate x rate. The exclusions get there by TWO different routes,
					// which is why this cannot be derived from one flag:
					//   T&M            passes isFixedPriceProject = false  -> accrues
					//   FIXED_PRICE    passes false too, via
					//                  CalculatePlannedFixedPriceOldFinancials -> accrues
					//   FIXED_PRICE_V2 passes true                          -> excluded
					//   RETAINER       ALWAYS excluded, both arms. FinancialLogic.ts:205
					//                  passes isFixedPriceProject = true LITERALLY for
					//                  the non-V2 arm, and the V2 arm reaches
					//                  calculatePlannedTimeMaterialFinancials with true
					//                  as well. defaultPeriodBudgetType never reaches
					//                  the accrual, so it cannot change the answer.
					//   NON_BILLABLE   passes FALSE, but TimeMaterialLogic's
					//                  `budgetType !== NON_BILLABLE` gate diverts the
					//                  price to nonBillablePlannedTimeAndExpenses
					// A figure on any excluded type would be confidently wrong rather
					// than merely absent.
					//
					// An earlier revision of PIKA-672 made RETAINER conditional on
					// defaultPeriodBudgetType, reasoning from ProjectUtil
					// .isFixedPriceProject. That helper answers how the PAGE classifies
					// a retainer, not whether addTaskPlanned accrues -- measured, a
					// retainer task adds 1687.50 to billablePlannedTimeAndExpenses and
					// 0 to plannedRevenue. The conditional version previewed a figure
					// the budget would never move by, so it is reverted here.
					budgetMovesWithEstimate={
						viewer.project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS ||
						viewer.project.budgetType === BUDGET_TYPE.FIXED_PRICE
					}
					// Feature hiding is client-side only and nothing in the pricing path
					// consults it, so without this a REVENUE-hidden tenant sees a drawer
					// quoting Plan revenue and an hourly rate while every money column on
					// the page behind it is gone.
					//
					// Deliberately NOT folded into budgetMovesWithEstimate: that prop
					// selects a REASON to display, and "your budget type does not accrue"
					// is false on a T&M project whose revenue is merely hidden. This one
					// suppresses the figure, the reason and the rate hint together,
					// saying nothing rather than something untrue.
					revenueVisible={ProjectUtil.projectTracksRevenue(viewer.project)}
					// Points arrive as points and are converted to minutes at the
					// service-company boundary (SimpleTaskResponseUtil.setTaskEstimates),
					// so the budget DOES accrue for a points project. The drawer needs
					// the same factor to preview the same number.
					minutesPerEstimationPoint={viewer.project.minutesPerEstimationPoint ?? null}
					// Kept SEPARATE from the budget type rather than folded into it:
					// TimeMaterialLogic.ts:143 also requires plannedRevenue === 'TASK',
					// and on an allocation-sourced project no task of any kind moves
					// planned revenue. One combined boolean forced one message to cover
					// both, which told a Finance Manager on an allocation-sourced T&M
					// project that the BUDGET TYPE was the reason -- false, and pointing
					// at the wrong setting.
					revenueSourcedFromTasks={viewer.project.financialSourceSettings?.plannedRevenue === 'TASK'}
					phaseName={phases.find(phase => phase.id === taskSetDrawerPhaseId)?.name ?? null}
					onClose={() => setTaskSetDrawerPhaseId(undefined)}
					// Called with the variable it would otherwise lose: refetch replaces
					// the container's variables wholesale, so a bare call resets
					// noStaleTasks to the root query's value and dormant tasks vanish
					// from the table while the menu still shows them as shown.
					onCreated={() => relay.refetch({noStaleTasks: !showResolvedTasks})}
				/>
			) : null}
		</div>
	);
};

const ProjectScopingPageQuerry = graphql`
	query ProjectScopingPage_Query(
		$projectId: String
		$noStaleTasks: Boolean
		$fetchLazyData: Boolean!
		$fetchCustomFields: Boolean!
		$personId: ID!
	) {
		viewer {
			actualPersonId
			component(name: "new_scoping")
			project(id: $projectId) {
				id
			}
			...ProjectScopingPage_viewer
				@arguments(
					projectId: $projectId
					noStaleTasks: $noStaleTasks
					fetchLazyData: $fetchLazyData
					fetchCustomFields: $fetchCustomFields
					personId: $personId
				)
		}
	}
`;

export {ProjectScopingPageQuerry};

export default withSocketHandling(
	withRouter(
		createRefetchContainer(
			ProjectScopingPage,
			{
				viewer: graphql`
					fragment ProjectScopingPage_viewer on Viewer
					@argumentDefinitions(
						projectId: {type: "String"}
						noStaleTasks: {type: "Boolean"}
						fetchLazyData: {type: "Boolean!"}
						fetchCustomFields: {type: "Boolean!"}
						personId: {type: "ID!"}
					) {
						id
						language
						email
						firstName
						lastName
						timerStartDate
						actualPersonId
						harvestUser
						unit4User
						excludeFromCompanyLockedPeriod
						submitLockedDateYear
						submitLockedDateMonth
						submitLockedDateDay
						startDate
						endDate
						createdAt
						monday
						tuesday
						wednesday
						thursday
						friday
						saturday
						sunday
						availableFeatureFlags {
							key
						}
						timerTask {
							id
						}
						company {
							...genericTaskContextMenu_company
							jiraCloudEnabled
							jiraServerEnabled
							sageIntacctConnectionEnabled
							integrations {
								jiraCloud {
									syncSettings {
										isJiraToForecastOneWaySync
									}
								}
								jiraServer {
									syncSettings {
										isJiraToForecastOneWaySync
									}
								}
							}
							modules {
								moduleType
							}
							person(id: $personId) {
								id
								role {
									id
									name
								}
							}
							isUsingProjectAllocation
							isUsingMixedAllocation
							holidayCalendars(first: 10000) @connection(key: "Company_holidayCalendars") {
								edges {
									node {
										id
										holidayCalendarEntries(first: 100000)
											@connection(key: "HolidayCalendar_holidayCalendarEntries") {
											edges {
												node {
													id
													name
													year
													month
													day
												}
											}
										}
									}
								}
							}
							allPersons(first: 1000000, onlyActive: true) @connection(key: "Company_allPersons", filters: []) {
								edges {
									node {
										id
										firstName
										lastName
									}
								}
							}
							id
							currency
							harvestEnabled
							unit4Enabled
							jiraCloudEnabled
							lockedPeriodYear
							lockedPeriodMonth
							lockedPeriodDay
							# Minutes per weekday, for the create drawer's non-working-day
							# warning. Zero means the company does not work that day -- the
							# same signal the backend's deadline walker reads. Distinct from
							# the viewer's own weekday hours selected above.
							monday
							tuesday
							wednesday
							thursday
							friday
							saturday
							sunday
							labels(first: 1000000, labelType: TASK) {
								edges {
									node {
										id
										name
										color
										category {
											id
											name
										}
										...LabelDropdownJS_labels
									}
								}
							}
							roles(first: 1000000) {
								edges {
									...RoleDropdown_roles
									node {
										id
										name
									}
								}
							}
							teams(first: 1000000) {
								edges {
									node {
										id
										name
										teamPersons(first: 1000) {
											edges {
												node {
													id
													person {
														id
													}
												}
											}
										}
									}
								}
							}
							customFieldDefinitionsV2(first: 1000)
								@connection(key: "Company_customFieldDefinitionsV2")
								@include(if: $fetchCustomFields) {
								edges {
									node {
										id
										key
										displayName
										entityType
										... on TextCustomField {
											readOnly
										}
										... on IntegerCustomField {
											readOnly
										}
										... on DecimalCustomField {
											readOnly
										}
										... on DateCustomField {
											readOnly
										}
									}
								}
							}
						}
						filters(first: 1000000, projectId: $projectId, filterSection: SCOPING)
							@connection(key: "Viewer_filters", filters: []) {
							edges {
								node {
									id
									name
									section
									value
									updatedAt
								}
							}
						}
						projects(first: 1000000, excludeRestricted: true) {
							edges {
								node {
									id
									companyProjectId
									customProjectId
									name
									status
									isInProjectGroup
									projectColor
								}
							}
						}
						project(id: $projectId) {
							...ProjectHeader_project
							...SecondaryNavigation_project
							...ManageTaskSetsDrawer_project
							projectFirstDateYear
							projectFirstDateMonth
							projectFirstDateDay
							projectLastDateYear
							projectLastDateMonth
							projectLastDateDay
							baselineTargetPrice
							baselineTargetMinutes
							id
							isNoStaleTasksAllowed
							name
							demo
							budgetWork
							budgetType
							defaultPeriodBudgetType
							budget
							billable
							synchBaselineAndScopingDates
							program {
								name
								prefix
								budgetType
								members {
									edges {
										node {
											role
											person {
												id
											}
										}
									}
								}
							}
							sprintTimeBox
							isInProjectGroup
							useBaseline
							projectGroupId
							status
							companyProjectId
							customProjectId
							estimationUnit
							minutesPerEstimationPoint
							timeRegistrations(first: 10000000, onlyProjectRegistrations: true)
								@connection(key: "Project_timeRegistrations", filters: [])
								@include(if: $fetchLazyData) {
								edges {
									node {
										id
										minutesRegistered
										billableMinutesRegistered
										invoiced
										price
										updatedAt
										year
										month
										day
										retainerConflictHandled
										task {
											id
										}
										person {
											id
										}
									}
								}
							}
							retainerPeriods(first: 100000) {
								edges {
									node {
										id
										name
										startYear
										startMonth
										startDay
										endYear
										endMonth
										endDay
										available
										periodLength
										periodPeriodicity
										periodPriceAmount
										periodHoursAmount
										periodBudgetType
										periodSettingIgnoreForBilling
										periodSettingSubtractValue
										periodSettingRollValue
										periodSettingAddExpenses
										periodDifferencePriceAmount
										periodDifferenceHoursAmount
										sharedPeriodDifferenceHoursAmount
										sharedPeriodDifferencePriceAmount
										ignoredRolloverHours
										ignoredRolloverPrice
										periodLocked
										periodLockedTime
									}
								}
							}
							minutesPerEstimationPoint
							projectColor
							projectStartYear
							projectStartMonth
							projectStartDay
							projectEndYear
							projectEndMonth
							projectEndDay
							useManualAllocations
							remainingAutoCalculated
							isJiraProject
							useTaskOwner
							useTaskFollowers
							taskLevels
							useTaskHierarchy
							useDeliverables
							portalConfig {
								showInClientPortal
								portalPhaseStatusVisible
							}
							deliverables(first: 100000) @connection(key: "Project_deliverables", filters: []) {
								edges {
									node {
										id
										name
										description
										price
										invoicedAmount
									}
								}
							}
							# Only which Deliverables have Baseline estimates, for Manage Deliverables' delete warning.
							phaseBaselineRoles(first: 100000) @include(if: $fetchLazyData) {
								edges {
									node {
										id
										deliverableId
										role {
											id
										}
									}
								}
							}
							phaseBaselineExpenses(first: 100000) @include(if: $fetchLazyData) {
								edges {
									node {
										id
										deliverableId
									}
								}
							}
							vstsProject
							vstsAccount
							harvestProjectId
							vstsTwoWaySync
							jiraSubtaskType
							progress
							manualProgressOnProjectEnabled
							manualProgressOnPhasesEnabled
							manualProgressOnTasksEnabled
							financialSourceSettings {
								plannedHours
								plannedRevenue
								plannedCost
								actualRevenue
								actualCost
								forecastRevenue
								actualHours
							}
							jiraCloudProject {
								id
							}
							jiraCloudEpicIds
							jiraServerProject {
								id
							}
							sageProject {
								sageProjectId
							}
							statusColumnsV2(first: 1000000) {
								edges {
									node {
										id
										name
										order
										jiraStatusId
										category
									}
								}
							}
							rateCard {
								id
								currency
								defaultRate
								rates(first: 10000) {
									edges {
										node {
											id
											rate
											defaultRate
											startDate
											role {
												id
											}
										}
									}
								}
								disabledRoles {
									id
								}
							}
							# FOR-525: the client workspace users offerable as a sign-off approver on
							# this project. Selected here, on the project the table is already
							# loading, so the whole task table shares ONE list — the approver
							# cell renders per row, and fetching inside it would be a request
							# per task.
							portalEligibleApprovers {
								portalUserId
								email
								roleLabel
							}
							projectPersons(first: 1000000) {
								edges {
									node {
										id
										role {
											id
											name
										}
										person {
											id
											firstName
											lastName
											fullName
											initials
											profilePictureId
											profilePictureDefaultId
											active
											monday
											tuesday
											wednesday
											thursday
											friday
											saturday
											sunday
											holidayCalendar {
												id
											}
											role {
												id
												name
											}
											client {
												id
											}
											allocations(first: 1000000, idleTimeOnly: true) {
												edges {
													node {
														id
														monday
														tuesday
														wednesday
														thursday
														friday
														saturday
														sunday
														startYear
														startMonth
														startDay
														endYear
														endMonth
														endDay
														idleTime {
															isInternalTime
														}
													}
												}
											}
										}
									}
								}
							}
							phases(first: 1000000) @connection(key: "Project_phases") {
								edges {
									node {
										baselineTargetPrice
										baselineTargetMinutes
										baselineCost
										inheritDatesFrom
										progress
										progressDetails {
											progress
										}
										phaseBaselineRoles {
											edges {
												node {
													role {
														id
													}
													baselineMinutes
													baselinePrice
												}
											}
										}
										id
										projectId
										name
										portalConfig {
											requiresClientSignoff
											signoffApproverPortalUserId
											signoffLocked
											signoffLockedAt
											signoffRequestState
											signoffRequestDeadline
											signoffRequestApproverPortalUserId
											signoffChangesRequestedNote
										}
										startYear
										startMonth
										startDay
										startFrom
										deadlineDay
										deadlineMonth
										deadlineYear
										deadlineFrom
										baselineStartYear
										baselineStartMonth
										baselineStartDay
										baselineDeadlineDay
										baselineDeadlineMonth
										baselineDeadlineYear
										unit4Id
										jiraId
										sortOrder
										phasePersons(first: 10000) {
											edges {
												node {
													id
													availableMinutes
													scheduledMinutes
													person {
														id
													}
												}
											}
										}
										customFields @include(if: $fetchCustomFields) {
											edges {
												node {
													__typename
													key
													displayName
													value
													type
													... on TextCustomFieldValue {
														readOnly
													}
													... on IntegerCustomFieldValue {
														readOnly
													}
													... on DecimalCustomFieldValue {
														readOnly
													}
													... on DateCustomFieldValue {
														readOnly
													}
													... on DropdownCustomFieldValue {
														options {
															id
															displayName
														}
													}
												}
											}
										}
									}
								}
							}
							baselineFinancialNumbers: groupedFinancialNumbers(
								convertToProjectCurrency: true
								groupBy: ["PHASE_BASELINE_ROLE", "PHASE"]
							) {
								baselineRevenue
								baselineTimeAndExpenses
								baselineCost
								baselineProfit
								baselineMargin
								baselineRatePerHour
								baselineCostPerHour
								baselineMinutes
								phaseBaselineRoleId
								phaseBaselineExpenseId
								phaseId
							}
							sprints(first: 1000000) {
								edges {
									node {
										id
										name
										endYear
										endMonth
										endDay
										startYear
										startMonth
										startDay
									}
								}
							}
							tasks(first: 100000, simpleResponse: true, noStaleTasks: $noStaleTasks)
								@connection(key: "Project_tasks", filters: []) {
								edges {
									node {
										id
										readOnly {
											isReadOnly
										}
										userCanDeleteTask
										userCantDeleteTaskReason
										hasInvoicedTime
										hasLockedTime
										hasTimeRegistrations
										approved
										name
										taskSetSequence
										taskSet {
											id
											instanceCount
											boundType
											boundCount
											frequency
											weekdays
											seedDate
										}
										description
										sortOrder
										done
										billable
										blocked
										progress @include(if: $fetchLazyData)
										bug
										latestUiUpdateAt
										startFrom
										deadlineFrom
										startYear
										startMonth
										startDay
										deadlineDay
										deadlineMonth
										deadlineYear
										estimateForecast
										estimateForecastMinutes
										estimateForecastPrice
										totalPriceAtCompletion
										totalCostAtCompletion
										allTotalTimeAndExpensesAtCompletion
										currentPrice @include(if: $fetchLazyData)
										actualCost @include(if: $fetchLazyData)
										plannedCost @include(if: $fetchLazyData)
										companyTaskId
										hasNovaConversation
										portalConfig {
											requiresClientSignoff
											signoffApproverPortalUserId
										}
										timeLeft @include(if: $fetchLazyData)
										deadlineFrom
										canStart
										canBeSetToDone
										approved
										highPriority
										hasDependency
										jiraId
										vstsId
										isUnit4Task
										subTaskCount
										parentTaskId
										deliverableId
										hasChildren
										sageIntacctId
										favoured
										progressDetails {
											progress
										}
										owner {
											id
											permissions
										}
										followers {
											id
											permissions
										}
										phase {
											id
											name
											startYear
											startMonth
											startDay
											startFrom
											deadlineDay
											deadlineMonth
											deadlineYear
											deadlineFrom
											portalConfig {
												requiresClientSignoff
												signoffApproverPortalUserId
											}
										}
										role {
											id
											name
										}
										sprint {
											id
											name
											startYear
											startMonth
											startDay
											endYear
											endMonth
											endDay
										}
										statusColumnV2 {
											id
											name
											category
										}
										project {
											id
											demo
											billable
											estimationUnit
											minutesPerEstimationPoint
											projectStartYear
											projectStartMonth
											projectStartDay
											projectEndYear
											projectEndMonth
											projectEndDay
											budgetType
											projectPerson(personId: $personId) {
												role {
													id
													name
												}
											}
											program {
												name
												prefix
												budgetType
												members {
													edges {
														node {
															role
															person {
																id
															}
														}
													}
												}
											}
											name
											status
											isJiraProject
											vstsProject
											vstsAccount
											vstsTwoWaySync
											projectColor
											companyProjectId
											customProjectId
											harvestProject {
												id
											}
											unit4Project {
												id
												activitiesEnabled
											}
											sprintTimeBox
										}
										taskLabels {
											id
											label {
												id
												name
												color
											}
										}
										timeRegistrations(first: 10000)
											@connection(key: "Task_timeRegistrations", filters: [])
											@include(if: $fetchLazyData) {
											edges {
												node {
													id
													minutesRegistered
													billableMinutesRegistered
													price
													invoiced
													year
													month
													day
													retainerConflictHandled
													person {
														id
														role {
															id
														}
													}
												}
											}
										}
										taskDismissedNotifications {
											taskOverrunPredictionDismissed
										}
										assignedPersons {
											id
											firstName
											lastName
											fullName
											active
											email
											profilePictureId
											profilePictureDefaultId
											permissions
										}
										thisTaskDependsOn(first: 1000) {
											edges {
												node {
													id
													type
													thisDependsOnTask {
														id
														name
														deadlineYear
														deadlineMonth
														deadlineDay
														startYear
														startMonth
														startDay
													}
												}
											}
										}
										customFields @include(if: $fetchCustomFields) {
											edges {
												node {
													__typename
													key
													displayName
													value
													type
													... on TextCustomFieldValue {
														readOnly
													}
													... on IntegerCustomFieldValue {
														readOnly
													}
													... on DecimalCustomFieldValue {
														readOnly
													}
													... on DateCustomFieldValue {
														readOnly
													}
													... on DropdownCustomFieldValue {
														options {
															id
															displayName
														}
													}
												}
											}
										}
									}
								}
							}
							allocations(first: 100000) {
								edges {
									node {
										id
										monday
										tuesday
										wednesday
										thursday
										friday
										saturday
										sunday
										startYear
										startMonth
										startDay
										endYear
										endMonth
										endDay
										person {
											id
											role {
												id
											}
											holidayCalendar {
												id
											}
										}
										project {
											id
										}
										isSoft
									}
								}
							}
							expenseFinancialNumbers: financialNumbers(
								searchQuery: {filters: [{field: ROLE, operator: IS, value: "Um9sZTotMQ=="}]}
								convertToProjectCurrency: true
							) {
								allTotalTimeAndExpensesAtCompletion
							}
							totalFinancialNumbers: financialNumbers(convertToProjectCurrency: true) {
								totalCostAtCompletion
								totalRevenueRecognition
								totalRevenueProfitAtCompletion
								totalRevenueMarginAtCompletion
								forecastMinutes
								registeredMinutes
								billableActualMinutes
								nonBillableActualMinutes
								billableTotalTimeAndExpensesAtCompletion
								baselineMinutes
								allocationMinutes
								scopeApprovedMinutes
								remainingMinutes
								baselineRevenue
								plannedCost
								actualCost
								projectedBillableMinutes
								projectedNonBillableMinutes
								projectedTotalMinutes
								plannedRevenue
								plannedProfit
								plannedMargin
								baselineMargin
								remainingCost
								actualRevenue
								baselineCost
								baselineProfit
								billableActualTimeAndExpenses
								nonBillableActualTimeAndExpenses
								baselineTimeAndExpenses
								allTotalTimeAndExpensesAtCompletion
								taskRegisteredMinutes
								projectRegisteredMinutes
							}
						}
						psProject(companyProjectId: $projectId) {
							...ProjectHeader_psProject
						}
					}
				`,
			},
			graphql`
				query ProjectScopingPageRefetchQuery(
					$projectId: String
					$noStaleTasks: Boolean
					$fetchLazyData: Boolean!
					$fetchCustomFields: Boolean!
					$personId: ID!
				) {
					viewer {
						component(name: "new_scoping_refetch")
						...ProjectScopingPage_viewer
							@arguments(
								projectId: $projectId
								noStaleTasks: $noStaleTasks
								fetchLazyData: $fetchLazyData
								fetchCustomFields: $fetchCustomFields
								personId: $personId
							)
					}
				}
			`
		)
	)
);
